import { z } from 'zod';
import { serviceCategorySchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema, isoDateSchema } from '../../common/schemas/money.schemas.js';
/**
 * Manual service history — what happened to the car **before** CTA owned it (PP §8).
 *
 * The one rule that matters here is the one the type system cannot state: `amount` is **evidence only**
 * and never enters any CTA figure (PP AC07). A 300 JOD gearbox repair from 2024 tells a buyer something
 * real about the car; it is not money this department is about to spend, and adding it to the projected
 * cost would inflate the required retail of a car that has already been repaired.
 *
 * That is why these rows live in their own table rather than as `cost_items` with a flag: the cost
 * pipeline reads `cost_items`, full stop, and a figure that must never be summed is safest in a place
 * the summing code cannot see.
 */
export const serviceEventSchema = z.object({
    id: uuidSchema,
    vehicleId: uuidSchema,
    eventDate: isoDateSchema,
    odometerKm: z.number().int().min(0).nullable(),
    provider: z.string(),
    category: serviceCategorySchema,
    description: z.string(),
    /** Historical evidence. Never a CTA cost (PP AC07). */
    amount: amountSchema.nullable(),
    /** The invoice or report that proves it, when one was attached. */
    documentId: uuidSchema.nullable(),
    createdBy: uuidSchema.nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listServiceEventsInputSchema = z.object({
    vehicleId: uuidSchema,
});
export const listServiceEventsOutputSchema = z.object({
    vehicleId: uuidSchema,
    items: z.array(serviceEventSchema),
    /** Σ of the recorded historical amounts — printed as evidence, never added to any cost. */
    evidenceTotal: z.number(),
});
export const addServiceEventInputSchema = z.object({
    vehicleId: uuidSchema,
    eventDate: isoDateSchema,
    odometerKm: z.number().int().min(0).max(9_999_999).nullish(),
    provider: z.string().trim().min(1, 'Name the garage or service centre').max(200),
    category: serviceCategorySchema,
    description: z.string().trim().min(1, 'Describe the work').max(2_000),
    amount: amountSchema.nullish(),
    documentId: uuidSchema.nullish(),
});
/** PATCH semantics; an absent key leaves the column alone. */
export const updateServiceEventInputSchema = z.object({
    id: uuidSchema,
    eventDate: isoDateSchema.optional(),
    odometerKm: z.number().int().min(0).max(9_999_999).nullish(),
    provider: z.string().trim().min(1, 'Name the garage or service centre').max(200).optional(),
    category: serviceCategorySchema.optional(),
    description: z.string().trim().min(1, 'Describe the work').max(2_000).optional(),
    amount: amountSchema.nullish(),
    documentId: uuidSchema.nullish(),
});
export const removeServiceEventInputSchema = z.object({
    id: uuidSchema,
});
//# sourceMappingURL=history.schemas.js.map